import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Save, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import { api } from "@/lib/api";

// `percent`: il motore salva una frazione (0.30), la pagina mostra 30.
const SETTING_KEYS: Array<{ key: string; label: string; help: string; percent?: boolean }> = [
  {
    key: "refresh_interval_min",
    label: "Intervallo aggiornamento",
    help: "Cadenza del gestore Mirakl in minuti. Min 5, max 1440.",
  },
  {
    key: "auto_apply.max_delta_pct",
    label: "Ribasso automatico massimo (%)",
    help: "AM abbassa da solo fino a questa percentuale per battere un concorrente, mai sotto il margine minimo. Oltre serve l'approvazione. Da 1 a 100, vuoto = 5.",
    percent: true,
  },
  {
    key: "alert.critical_diff_eur",
    label: "Mail: euro sopra il concorrente",
    help: "Un ribasso da approvare va in mail se siamo sopra il concorrente di almeno questi euro. Vuoto = 50.",
  },
  {
    key: "alert.critical_margin_eur",
    label: "Mail: margine recuperabile in euro",
    help: "Oppure se il nuovo prezzo lascia almeno questi euro di margine. Vuoto = 50.",
  },
  {
    key: "alert.min_email_interval_hours",
    label: "Mail: ore fra due mail",
    help: "Al massimo una mail ogni tot ore. Da 1 a 168, vuoto = 6.",
  },
  {
    key: "alert.renotify_days",
    label: "Mail: giorni prima di ripetere un prodotto",
    help: "Un prodotto gia' segnalato torna in mail dopo questi giorni. Da 1 a 90, vuoto = 7.",
  },
];

function toDisplay(raw: string | undefined, percent?: boolean): string {
  if (raw === undefined || raw === "") return "";
  if (!percent) return raw;
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? String(Math.round(n * 10000) / 100) : raw;
}

function toStored(shown: string, percent?: boolean): string {
  if (!percent) return shown;
  const n = Number(shown.replace(",", "."));
  return Number.isFinite(n) ? String(Math.round(n * 100) / 10000) : shown;
}

const CRED_KEYS: Array<{ key: string; label: string }> = [
  { key: "mdm_api_key", label: "Chiave API Mirakl (Maisons du Monde)" },
  { key: "leroy_merlin_api_key", label: "Chiave API Mirakl (Leroy Merlin)" },
  { key: "vente_unique_api_key", label: "Chiave API Mirakl (Vente Unique)" },
  { key: "poleepo_client_id", label: "Poleepo client_id" },
  { key: "poleepo_client_secret", label: "Poleepo client_secret" },
];

export function Settings(): JSX.Element {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const [draft, setDraft] = useState<Record<string, string>>({});

  // Si salvano solo i campi cambiati: rimandare tutto rischiava di far
  // rifiutare il salvataggio per un valore vecchio non piu' valido.
  const saveSettings = useMutation({
    mutationFn: (values: Record<string, string>) => api.putSettings(values),
    onSuccess: () => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
  const saveCredential = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => api.storeCredential(key, value),
  });
  const percentKeys = new Set(SETTING_KEYS.filter((k) => k.percent).map((k) => k.key));

  return (
    <section className="space-y-6 px-7 pb-8 pt-6">
      <header className="max-w-2xl space-y-1">
        <h1 className="page-title">Impostazioni</h1>
        <p className="page-subtitle">
          Parametri operativi e credenziali salvate in Gestione Credenziali Windows.
        </p>
      </header>

      {settings.isError && (
        <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore caricamento impostazioni: {String(settings.error)}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="card-stack p-5">
          <div className="mb-5 flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-ink-3" strokeWidth={1.7} aria-hidden="true" />
            <h2 className="text-lg font-semibold tracking-tight text-foreground">Parametri</h2>
          </div>
          <div className="space-y-4">
            {SETTING_KEYS.map(({ key, label, help, percent }) => {
              const current = draft[key] ?? toDisplay(settings.data?.values[key], percent);
              return (
                <div key={key}>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm font-medium" htmlFor={`s-${key}`}>
                      {label}
                    </label>
                    <span className="font-mono text-xs text-muted-foreground">{key}</span>
                  </div>
                  <input
                    id={`s-${key}`}
                    type="text"
                    className="mt-1 min-h-11 w-full rounded-[var(--radius)] border border-line-2 bg-card px-3 py-2 font-mono text-md text-foreground outline-none transition focus:border-ink focus:bg-white"
                    value={current}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{help}</p>
                </div>
              );
            })}
            <button
              type="button"
              disabled={saveSettings.isPending || Object.keys(draft).length === 0}
              onClick={() =>
                saveSettings.mutate(
                  Object.fromEntries(
                    Object.entries(draft).map(([k, v]) => [k, toStored(v, percentKeys.has(k))]),
                  ),
                )
              }
              className="btn-editorial-primary min-h-10 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" strokeWidth={1.5} />
              {saveSettings.isPending ? "Salvataggio..." : "Salva parametri"}
            </button>
            {saveSettings.isError && (
              <p className="text-sm text-destructive" role="alert">
                Non salvato: {String(saveSettings.error)}
              </p>
            )}
            {saveSettings.isSuccess && Object.keys(draft).length === 0 && (
              <p className="text-sm text-muted-foreground" role="status">
                Salvato.
              </p>
            )}
          </div>
        </section>

        <section className="card-stack p-5">
          <div className="mb-5 flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-ink-3" strokeWidth={1.7} aria-hidden="true" />
            <h2 className="text-lg font-semibold tracking-tight text-foreground">Credenziali</h2>
          </div>
          <div className="space-y-4">
            {CRED_KEYS.map(({ key, label }) => (
              <CredentialRow
                key={key}
                k={key}
                label={label}
                onSave={(value, done) =>
                  saveCredential.mutate({ key, value }, { onSuccess: done })
                }
                saving={saveCredential.isPending}
              />
            ))}
          </div>
          {saveCredential.isError && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              Credenziale non salvata: {String(saveCredential.error)}
            </p>
          )}
          <p className="panel-soft mt-5 px-3 py-3 text-xs text-muted-foreground">
            I segreti vengono scritti nel vault di Windows e non sono mai riletti in chiaro dal
            backend.
          </p>
        </section>
      </div>
    </section>
  );
}

function CredentialRow({
  k,
  label,
  onSave,
  saving,
}: {
  k: string;
  label: string;
  onSave: (value: string, done: () => void) => void;
  saving: boolean;
}): JSX.Element {
  const [value, setValue] = useState("");
  return (
    <div>
      <label className="block text-sm font-medium" htmlFor={`c-${k}`}>
        {label}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          id={`c-${k}`}
          type="password"
          autoComplete="off"
          className="min-h-11 min-w-0 flex-1 rounded-[var(--radius)] border border-line-2 bg-card px-3 py-2 font-mono text-md text-foreground outline-none transition focus:border-ink focus:bg-white"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          type="button"
          disabled={saving || !value}
          onClick={() => onSave(value, () => setValue(""))}
          className="btn-editorial-outline min-h-11 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save className="h-4 w-4" strokeWidth={1.5} />
          Salva
        </button>
      </div>
    </div>
  );
}
